"use client";
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  ResponsiveContainer,
  type TooltipContentProps,
  LabelList,
  Cell,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { BillingService } from "@/services/BillingService";
import { useEffect, useState } from "react";
import type { BillingsProductsProfitOutput } from "@/interfaces/billing";
import { ChartTooltip } from "@/components/ui/chart";
import { numberToMoney } from "@/util/moneyConversion";
import { Skeleton } from "@/components/ui/skeleton";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Percent, DollarSign } from "lucide-react";
import {
  BaseComponentProps,
  LabelProps,
  ProductChartDataItem,
} from "@/interfaces/dashboard";
import { FinancialColors } from "@/util/chartColors";

export function ProductsProfitChart({
  monthYear,
  isLoading = false,
}: BaseComponentProps) {
  const [productProfit, setProductProfit] = useState<
    BillingsProductsProfitOutput[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalProfit, setTotalProfit] = useState<number>(0);
  const [averageMargin, setAverageMargin] = useState<number>(0);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const service = new BillingService();
      const response = await service.getProductsProfit({ monthYear });
      setProductProfit(response);

      // Calcular lucro total e margem média
      if (response.length > 0) {
        const totalProfitValue = response.reduce(
          (sum, product) => sum + product.totalProfit,
          0
        );
        const totalAmount = response.reduce(
          (sum, product) => sum + product.totalAmount,
          0
        );

        setTotalProfit(totalProfitValue);
        setAverageMargin(
          totalAmount > 0 ? (totalProfitValue / totalAmount) * 100 : 0
        );
      }
    } catch {
      setError("Falha ao carregar dados de lucro por produto");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [monthYear]);

  const chartData: ProductChartDataItem[] = productProfit?.map((product) => {
    const costAmount = product.totalAmount - product.totalProfit;
    const profitPercentage = (product.totalProfit / product.totalAmount) * 100;
    const costPercentage = 100 - profitPercentage;

    return {
      name: product.productName,
      totalAmount: product.totalAmount,
      profit: product.totalProfit,
      cost: costAmount,
      profitPercentage: profitPercentage.toFixed(1),
      costPercentage: costPercentage.toFixed(1),
      quantity: product.quantity,
    };
  });

  const CustomTooltip = ({ active, payload }: TooltipContentProps<number, string>) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ProductChartDataItem;
      return (
        <div className="bg-white p-4 border rounded shadow">
          <p className="font-bold mb-1">{data.name}</p>
          <p>Faturamento: {numberToMoney(data.totalAmount)}</p>
          <p>
            Lucro: {numberToMoney(data.profit)} ({data.profitPercentage}%)
          </p>
          <p>
            Custo: {numberToMoney(data.cost)} ({data.costPercentage}%)
          </p>
          <p>Quantidade: {data.quantity}</p>
        </div>
      );
    }
    return null;
  };

  const renderTotalLabel = (props: LabelProps) => {
    const x = Number(props.x) || 0;
    const y = Number(props.y) || 0;
    const width = Number(props.width) || 0;

    return (
      <text
        x={x + width / 2}
        y={y - 10}
        fill="#333"
        textAnchor="middle"
        dominantBaseline="text-after-edge"
        fontSize={12}
        fontWeight="bold"
      >
        {numberToMoney(props.value)}
      </text>
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <CardTitle className="flex items-center">
          <DollarSign className="h-5 w-5 mr-2 text-orange-500" />
          Lucro por Produto
        </CardTitle>
        <CardDescription>
          {new Date(
            `${monthYear.split("/")[1]}-${monthYear.split("/")[0]}-01`
          ).toLocaleDateString("pt-BR", {
            month: "long",
            year: "numeric",
          })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading || loading ? (
          <div className="flex justify-center items-center h-64">
            <Skeleton className="h-[350px] w-full rounded-md" />
          </div>
        ) : error ? (
          <div className="flex justify-center items-center h-64 text-red-500">
            {error}
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex justify-center items-center h-64 text-muted-foreground">
            Nenhum dado disponível para este período
          </div>
        ) : (
          <div>
            <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
              <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0">
                <div>
                  <span className="text-sm text-muted-foreground">
                    Lucro Total:
                  </span>
                  <span className="ml-2 font-semibold text-green-600">
                    {numberToMoney(totalProfit)}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-sm text-muted-foreground">
                    Margem Média:
                  </span>
                  <Badge
                    variant="outline"
                    className="ml-2 bg-green-50 flex items-center"
                  >
                    <Percent className="h-3 w-3 mr-1 text-green-600" />
                    <span className="text-green-600">
                      {averageMargin.toFixed(1)}%
                    </span>
                  </Badge>
                </div>
              </div>
              <Badge variant="outline" className="bg-orange-50">
                {chartData.length} produtos
              </Badge>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={chartData}
                  margin={{ top: 40, right: 30, left: 20, bottom: 20 }}
                  barSize={50}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="name"
                    tickLine={false}
                    tickMargin={10}
                    axisLine={false}
                    tickFormatter={(value: string) =>
                      value.length > 10 ? `${value.slice(0, 10)}...` : value
                    }
                  />
                  <ChartTooltip content={CustomTooltip} />

                  {/* Barra de Custo */}
                  <Bar
                    dataKey="cost"
                    name="Custo"
                    stackId="a"
                    fill={FinancialColors.expense}
                    radius={[0, 0, 0, 0]}
                    animationDuration={1500}
                  >
                    {chartData?.map((_, index) => (
                      <Cell
                        key={`cost-cell-${index}`}
                        fill={FinancialColors.expense}
                      />
                    ))}
                  </Bar>

                  {/* Barra de Lucro */}
                  <Bar
                    dataKey="profit"
                    name="Lucro"
                    stackId="a"
                    fill={FinancialColors.profit}
                    radius={[8, 8, 0, 0]}
                    animationDuration={1500}
                    animationBegin={300}
                  >
                    {chartData?.map((_, index) => (
                      <Cell
                        key={`profit-cell-${index}`}
                        fill={FinancialColors.profit}
                      />
                    ))}
                  </Bar>

                  <LabelList
                    dataKey="totalAmount"
                    position="top"
                    content={(props) => renderTotalLabel(props as LabelProps)}
                  />
                </BarChart>
              </ResponsiveContainer>
            </motion.div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

